import { useState, useEffect } from 'react';
import { container } from 'tsyringe';
import { useScript } from '@utils/hooks';
import { IAuthService } from '@base/features/auth/model/interfaces';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import { MAIN_ROUTES } from '@base/features/routing/constants/mainRoutes';
import { ResponseCode } from '@base/utils/constants';
import { getRequiredEnv, t } from '@base/utils/helpers';
import { useHistory } from 'react-router-dom';
import { FromSocial, OAuthProvider } from '../model/const';
import { checkUsername, saveResponse } from '../utils/helpers';
import { User } from '../model/interfaces';

export interface FaceBookLoginState {
  isProcessing: boolean;
  isLoggedIn: boolean;
  currentUser: User;
  loaded: boolean;
  token?: string;
}

export interface FaceBookLoginProps {
  appId: string;
  language: string;
  version: string;
  fields: string;
  onFailure?: (response: any) => void;
}

const facebookProps = {
  appId: getRequiredEnv('NX_FACEBOOK_APP_ID'),
  language: 'EN',
  version: 'v12.0',
  fields: 'email, id, name, picture',
  onFailure: (error: any) => {
    console.error('onFailure', error);
  },
};

const getUserInfo = (
  props: {
    appId: string;
    onFailure: (error: any) => void;
    language: string;
    fields: string;
    version: string;
  },
  state: FaceBookLoginState,
  setState: (state: FaceBookLoginState) => void,
  token?: string
): void => {
  (window as any).FB.api('/me', { locale: props.language, fields: props.fields }, (response: any) => {
    const currentUser: User = response;
    setState({
      ...state,
      isLoggedIn: true,
      currentUser,
      isProcessing: false,
      loaded: true,
      token: token,
    });
  });
};

const checkLoginCallback = (
  response: any,
  props: {
    appId: string;
    onFailure: (error: any) => void;
    language: string;
    fields: string;
    version: string;
  },
  state: FaceBookLoginState,
  setState: (state: FaceBookLoginState) => void
): void => {
  if (response.status === 'connected') {
    getUserInfo(props, state, setState);
  } else {
    setState({
      ...state,
      isLoggedIn: false,
      isProcessing: false,
    });
  }
};

const setFacebookAsyncInit = (
  props: {
    appId: string;
    onFailure: (error: any) => void;
    language: string;
    fields: string;
    version: string;
  },
  state: FaceBookLoginState,
  setState: (state: FaceBookLoginState) => void
): void => {
  (window as any).fbAsyncInit = () => {
    (window as any).FB.init({
      appId: props.appId,
      autoLogAppEvents: true,
      cookie: false,
      xfbml: false,
      version: props.version,
    });

    setState({ ...state, loaded: true });
    //Нужно ли нам сразу проверять? Или лучше пусть входит каждый раз?
    (window as any).FB.getLoginStatus((response: unknown) => {
      checkLoginCallback(response, props, state, setState);
    });
  };
};

const loginCallback = (
  response: any,
  props: {
    appId: string;
    onFailure: (error: any) => void;
    language: string;
    fields: string;
    version: string;
  },
  state: FaceBookLoginState,
  setState: (state: FaceBookLoginState) => void
): void => {
  if (response.authResponse) {
    getUserInfo(props, state, setState, response.authResponse.accessToken);
  } else {
    if (props.onFailure) {
      props.onFailure(response);
      setState({ ...state, isProcessing: false });
    }
  }
};

const logoutCallback = (
  response: any,
  props: {
    appId: string;
    onFailure: (error: any) => void;
    language: string;
    fields: string;
    version: string;
  },
  state: FaceBookLoginState,
  setState: (state: FaceBookLoginState) => void
): void => {
  if (response.authResponse) {
    setState({
      ...state,
      isLoggedIn: false,
      currentUser: {
        id: '',
        name: '',
        email: '',
      },
      isProcessing: false,
    });
  } else {
    if (props.onFailure) {
      props.onFailure(response);
    }
  }
};
type UseFacebookLogin = {
  state: FaceBookLoginState;
  login: () => void;
  logout: () => void;
  checkLogin: () => void;
};

export function useFacebookLogin(): UseFacebookLogin {
  useScript('https://connect.facebook.net/en_US/sdk.js#version=v12.0', {
    async: true,
    defer: true,
    crossorigin: 'anonymous',
  });
  const [state, setState] = useState<FaceBookLoginState>({
    isProcessing: false,
    isLoggedIn: false,
    loaded: false,
    currentUser: {
      id: '',
      name: '',
      email: '',
    },
  });

  const authService: IAuthService = container.resolve('authService');
  const modalProvider: IModalProvider = container.resolve('modalProvider');
  const history = useHistory();

  const goFacebookPage = (): void => {
    const fromFacebook: FromSocial = {
      from: OAuthProvider.FACEBOOK,
    };
    history.push(MAIN_ROUTES.changeSocialUsername, fromFacebook);
  };

  const login = (): void => {
    setState({ ...state, isProcessing: true });
    (window as any).FB?.login((response: any) => loginCallback(response, facebookProps, state, setState), {
      scope: 'public_profile,email',
    });
  };

  const logout = (): void => {
    setState({ ...state, isProcessing: true });
    (window as any).FB.logout((response: any) => {
      logoutCallback(response, facebookProps, state, setState);
    });
  };

  useEffect(() => {
    setState({
      ...state,
      isProcessing: true,
    });

    setFacebookAsyncInit(facebookProps, state, setState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facebookProps.appId, facebookProps.fields, facebookProps.language, facebookProps.version]);

  const checkLogin = () => {
    (window as any).FB.getLoginStatus((response: any) => {
      console.log(response);
      if (response.status === 'connected') {
        // The user is logged in and has authenticated your
        // app, and response.authResponse supplies
        // the user's ID, a valid access token, a signed
        // request, and the time the access token
        // and signed request each expire.
        const uid = response.authResponse.userID;
        const accessToken = response.authResponse.accessToken;
        authService.doLoginFacebook(accessToken, state.currentUser.email);
      } else if (response.status === 'not_authorized') {
        console.log('Not authorized');
      } else {
        console.log('Not logged in to facebook');
      }
    });
  };

  useEffect(() => {
    if (state.currentUser && state.currentUser.email && state.token) {
      setState({
        ...state,
        isProcessing: true,
      });
      authService
        .doLoginFacebook(state.token, state.currentUser.email)
        .then((res: any) => {
          if (res.code === ResponseCode.SUCCESS) {
            const { usr } = res;
            if (checkUsername(usr)) {
              const data = {
                res,
                user: state.currentUser,
              };
              saveResponse(data);
              goFacebookPage();
            } else {
              authService.doEntryDefault(res);
            }
          } else {
            modalProvider.show({
              title: t('error'),
              body: t('unknown_error_try_again'),
              buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
            });
          }
        })
        .catch(() => {
          setState({
            ...state,
            isProcessing: false,
          });
          modalProvider.show({
            title: t('error'),
            body: t('unknown_error_try_again'),
            buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
          });
        });
    } else {
      setState({
        ...state,
        isProcessing: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentUser.id]);

  return { state, login, logout, checkLogin };
}
